import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { OrigamiHttpResponse, OrigamiResponse, ServiceBase } from './base/service.base';
import { DashboardType } from '../constants/dashboard-types';
import { DashboardOwnerType } from '../constants/dashboard-owner-types';
import GetTIAnalyticsUrlInfoResult = DashboardServiceResults.GetTIAnalyticsUrlInfoResult;
import GetTIAnalyticsClientsResult = DashboardServiceResults.GetTIAnalyticsClientsResult;
import GetTIAnalyticsActiveDashboardsResult = DashboardServiceResults.GetTIAnalyticsActiveDashboardsResult;
import GetTIAnalyticsActiveDashboardsOptions = DashboardServiceOptions.GetTIAnalyticsActiveDashboardsOptions;

export interface DashboardModel {
  DashboardConfiguration: string;
  DashboardTitle: string;
  DashboardId?: number;
  DashboardTypeId: number;
  Module: string;
  OwnerId: number;
  OwnerTypeId: number;
}

export namespace DashboardServiceOptions {
  export interface GetDashboardsOptions {
    siteId: number;
    ownerId: number;
    ownerTypeId?: number;
    dashboardTypeId?: number;
    isConfigMode?: boolean;
  }

  export interface SetDashboardOptions {
    dashboard: DashboardModel;
    siteId: number;
  }

  export interface GetTIAnalyticsUrlInfoOptions {
    sponsorId: number;
    dashboardId?: string;
    roomIds?: number[];
  }

  export interface GetTIAnalyticsActiveDashboardsOptions {
    sponsorId: number;
  }
}

export namespace DashboardServiceResults {
  export interface GetTIAnalyticsUrlInfoResult {
    EmbedUrl: string;
  }

  export interface GetTIAnalyticsClientsResult {
    SponsorName: string;
    SponsorId: string;
  }

  export interface GetTIAnalyticsActiveDashboardsResult {
    DashboardId: string;
    DashboardName: string;
  }
}

@Injectable()
export class DashboardService extends ServiceBase {
  serviceUrl = 'DashboardService.svc';

  getDashboards(
    options: DashboardServiceOptions.GetDashboardsOptions,
    ...params
  ): Observable<OrigamiResponse<DashboardModel[]>> {
    if (!options.ownerTypeId) {
      options.ownerTypeId = options.isConfigMode ? DashboardOwnerType.Role : DashboardOwnerType.User;
    }

    if (!options.dashboardTypeId) {
      options.dashboardTypeId = options.isConfigMode ? DashboardType.System : DashboardType.Custom;
    }

    return this.origamiPost('/GetDashboards', options, ...params);
  }

  getChartInfo(chartTypeIds: number[], partsLimit: number = 0, ...params): Observable<OrigamiHttpResponse> {
    const body = {
      chartTypeIds: chartTypeIds.join(','),
      partsLimit: partsLimit,
    };
    return this.origamiPost('/GetChartInfo', body, ...params);
  }

  setDashboard(options: DashboardServiceOptions.SetDashboardOptions): Observable<OrigamiResponse<number[]>> {
    return this.origamiPost('/SetDashboard', options, null, null, true);
  }

  getTIAnalyticsUrlInfo(
    options: DashboardServiceOptions.GetTIAnalyticsUrlInfoOptions,
    silentErrorCodes: number[] = [],
  ): Observable<OrigamiResponse<GetTIAnalyticsUrlInfoResult>> {
    return this.origamiPost('/GetTIAnalyticsUrlInfo', options, null, silentErrorCodes, true);
  }

  getTIAnalyticsClients(): Observable<OrigamiResponse<GetTIAnalyticsClientsResult[]>> {
    return this.origamiGet('/GetTIAnalyticsClients');
  }

  getTIAnalyticsActiveDashboards(
    options: GetTIAnalyticsActiveDashboardsOptions,
  ): Observable<OrigamiResponse<GetTIAnalyticsActiveDashboardsResult[]>> {
    return this.origamiPost('/GetTIAnalyticsActiveDashboards', options, null, null, true);
  }
}
